/**
 * BriefLoader — fetch + inject the engine's rendered course-brief HTML
 * into the PWA's #brief-panel. (BEACON-34 Phase 2)
 *
 * The engine produces self-contained HTML files (currently checked into
 * `pwa/public/briefs/<slug>/latest.html` via `scripts/sync-briefs.sh`).
 * Each file has its own `<head><style>…</style></head>` block + a `<body>`
 * with refresh-bar/FAB chrome we don't want inside the PWA shell.
 *
 * This loader:
 *   1. Fetches the brief HTML
 *   2. Parses it with DOMParser
 *   3. Strips the engine's own refresh-bar / refresh-fab (PWA owns refresh UX now)
 *   4. Scopes engine CSS so it doesn't leak — `body { … }` becomes `#brief-panel { … }`,
 *      and unscoped element selectors get prefixed.
 *   5. Injects the scoped style + body content into #brief-panel
 *
 * Phase 3+ will replace the static fetch with a Cloudflare Worker endpoint that
 * runs the engine on-demand against fresh weather data.
 */

const DEFAULT_BRIEF_URL = "/briefs/crandon-golf/latest.html";

// CSS prefix: every rule is scoped to live under #brief-panel so engine
// styles can't bleed into the PWA shell.
const SCOPE_SELECTOR = "#brief-panel";

export class BriefLoader {
  /**
   * @param {Object} opts
   * @param {HTMLElement} opts.panel - the #brief-panel element to inject into
   * @param {string} [opts.briefUrl] - URL of the rendered brief HTML
   * @param {number} [opts.holeCount=18] - per-course hole count (Shorty's=19, Bandon Preserve=13, regulation=18)
   */
  constructor({ panel, briefUrl = DEFAULT_BRIEF_URL, copy = null, courseDisplayName = null, holeCount = 18 }) {
    if (!panel) throw new Error("BriefLoader: panel element required");
    this.panel = panel;
    this.briefUrl = briefUrl;
    this.copy = copy;
    this.courseDisplayName = courseDisplayName;
    // BEACON-53 (audit fix): per-course hole bound; default 18 only as fallback.
    this.holeCount = Number.isFinite(holeCount) && holeCount > 0 ? holeCount : 18;
    this._styleEl = null; // injected <style> element, kept around for swap
  }

  async load() {
    let html;
    try {
      const resp = await fetch(this.briefUrl, { cache: "no-cache" });
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      html = await resp.text();
    } catch (err) {
      // BEACON-50 v0.7 (UX-F2): editorial error UX with retry + course-switch escape hatch.
      this._renderError(err);
      throw err;
    }

    const doc = new DOMParser().parseFromString(html, "text/html");
    const styleText = this._extractStyles(doc);
    const bodyContent = this._extractBody(doc);
    const meta = this._extractMeta(doc);

    this._renderStyle(styleText);
    this.panel.innerHTML = bodyContent;
    this.panel.dataset.briefSource = this.briefUrl;
    if (meta.course) this.panel.dataset.course = meta.course;
    if (meta.date) this.panel.dataset.date = meta.date;
    if (meta.tee) this.panel.dataset.tee = meta.tee;

    // Phase A: tag each .hole block with id="hole-N" + data-hole-number,
    // and build the registry of present holes for the HolePicker.
    this.presentHoles = this._tagHoles();

    console.info(
      `[BriefLoader] loaded ${this.briefUrl} — course=${meta.course} date=${meta.date} tee=${meta.tee} holes=[${this.presentHoles.join(",")}]`
    );

    // Surface the registry as a custom event for downstream consumers (HolePicker).
    this.panel.dispatchEvent(
      new CustomEvent("brief:loaded", {
        bubbles: true,
        detail: { meta, presentHoles: this.presentHoles },
      })
    );

    return meta;
  }

  /**
   * Phase A: walk the freshly-injected brief body, locate each `.hole`
   * block (engine output) OR pandoc-rendered hole heading (stub output),
   * and tag it with `id="hole-N"` + `data-hole-number="N"` so we can
   * scrollIntoView() / classList.add('is-current-hole') / etc. on it
   * when the HolePicker fires a holechange event.
   *
   * Detection passes (in order, deduped by hole number):
   *   1. Engine-rendered: `.hole > h3` containing `Hole N` text.
   *   2. Pandoc-rendered: any heading (h2/h3/h4) whose `id` starts with
   *      `hole-N` (e.g. `<h4 id="hole-2-par-4-368-yd-...">`). The heading
   *      element itself is tagged as the scroll target since the pandoc
   *      output has no surrounding `.hole` wrapper.
   *   3. Pandoc-rendered fallback: any heading whose text begins with
   *      `HOLE N` / `**HOLE N` / `#N` (RF v3 brief format).
   *
   * Returns the sorted list of hole numbers actually present in the brief.
   */
  _tagHoles() {
    const present = new Set();

    // Pass 1: engine-rendered .hole blocks
    const blocks = this.panel.querySelectorAll(".hole");
    for (const block of blocks) {
      const heading = block.querySelector("h3");
      if (!heading) continue;
      const m = heading.textContent?.match(/Hole\s+(\d{1,2})/i);
      if (!m) continue;
      const n = Number.parseInt(m[1], 10);
      if (!Number.isFinite(n) || n < 1 || n > this.holeCount) continue;
      if (present.has(n)) continue;
      block.id = `hole-${n}`;
      block.dataset.holeNumber = String(n);
      present.add(n);
    }

    // Pass 2: pandoc-rendered headings with id="hole-N-..."
    const headings = this.panel.querySelectorAll("h2[id], h3[id], h4[id]");
    for (const h of headings) {
      const idMatch = h.id.match(/^hole-(\d{1,2})(?:-|$)/i);
      if (!idMatch) continue;
      const n = Number.parseInt(idMatch[1], 10);
      if (!Number.isFinite(n) || n < 1 || n > this.holeCount) continue;
      if (present.has(n)) continue;
      // Replace id so anchor matches `#hole-N` exactly (not the pandoc slug).
      // Add `.hole` class so existing CSS + cleanup queries find this element.
      h.id = `hole-${n}`;
      h.dataset.holeNumber = String(n);
      h.classList.add("hole");
      present.add(n);
    }

    // Pass 3: pandoc-rendered text-only headings (RF v3 brief uses
    // `**HOLE N — ...**` which pandoc may render as <h4>HOLE N — ...</h4>
    // without a useful id when no leading `### ` is present).
    const allHeadings = this.panel.querySelectorAll("h2, h3, h4");
    for (const h of allHeadings) {
      if (h.id && /^hole-\d{1,2}$/i.test(h.id)) continue; // already tagged
      const txt = h.textContent || "";
      const m = txt.match(/^\s*(?:#|\*\*)?\s*HOLE\s+(\d{1,2})\b/i);
      if (!m) continue;
      const n = Number.parseInt(m[1], 10);
      if (!Number.isFinite(n) || n < 1 || n > this.holeCount) continue;
      if (present.has(n)) continue;
      h.id = `hole-${n}`;
      h.dataset.holeNumber = String(n);
      h.classList.add("hole");
      present.add(n);
    }

    // Pass 4: pandoc-rendered paragraph-style hole markers used by RF v3 briefs
    // for bandon-dunes, bandon-trails, sheep-ranch, bandon-preserve, shortys.
    // Format: `<p><strong>#N · Par X · Y yd · ...</strong></p>` rendered
    // from markdown `**#N · Par X · Y yd · ...**`. We tag the wrapping <p>
    // since there's no surrounding heading element. CSS treats `[data-hole-number]`
    // as the current-hole anchor regardless of element type.
    const paras = this.panel.querySelectorAll("p");
    for (const p of paras) {
      if (p.id && /^hole-\d{1,2}$/i.test(p.id)) continue;
      // Look at the first <strong> child's text content
      const strong = p.querySelector("strong");
      if (!strong) continue;
      const txt = strong.textContent || "";
      const m = txt.match(/^\s*#(\d{1,2})\s*[\u00b7\.\)]/);
      if (!m) continue;
      const n = Number.parseInt(m[1], 10);
      if (!Number.isFinite(n) || n < 1 || n > this.holeCount) continue;
      if (present.has(n)) continue;
      p.id = `hole-${n}`;
      p.dataset.holeNumber = String(n);
      p.classList.add("hole");
      present.add(n);
    }

    return Array.from(present).sort((a, b) => a - b);
  }

  /**
   * Phase A: scroll the panel to a given hole and add `.is-current-hole` to it.
   * If the hole has no section in the current brief, render a transient ghost
   * "Hole N — no specific notes today" block so the user gets feedback.
   *
   * Returns true if a real hole block existed; false if a ghost was rendered.
   */
  showHole(n, { scroll = true } = {}) {
    if (!Number.isFinite(n) || n < 1 || n > this.holeCount) return false;

    // Clear previous current-hole state
    this.panel
      .querySelectorAll(".hole.is-current-hole")
      .forEach((el) => el.classList.remove("is-current-hole"));
    const oldGhost = this.panel.querySelector("[data-hole-ghost]");
    if (oldGhost) oldGhost.remove();

    // BEACON-50 P1 (UX): respect prefers-reduced-motion for vestibular a11y.
    const prefersReducedMotion =
      typeof window !== "undefined" &&
      window.matchMedia &&
      window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    const scrollBehavior = prefersReducedMotion ? "auto" : "smooth";

    const target = this.panel.querySelector(`#hole-${n}`);
    if (target) {
      target.classList.add("is-current-hole");
      if (scroll) target.scrollIntoView({ behavior: scrollBehavior, block: "start" });
      return true;
    }

    // Ghost: hole exists conceptually but engine emitted no annotations for it today.
    const ghost = document.createElement("div");
    ghost.className = "hole is-current-hole hole-ghost";
    ghost.dataset.holeGhost = "true";
    ghost.dataset.holeNumber = String(n);
    ghost.innerHTML = `
      <div class="hole-header">
        <h3>Hole ${n}</h3>
        <span class="hole-meta">no specific notes today</span>
      </div>
      <p class="hole-ghost-msg">No notes for Hole ${n} today — see course-wide guidance above.</p>
    `;

    // Insert at top of the hole-by-hole section if it exists, else at end of panel
    const holeByHole = this.panel.querySelector("h2");
    if (holeByHole && /hole-by-hole/i.test(holeByHole.textContent || "")) {
      holeByHole.insertAdjacentElement("afterend", ghost);
    } else {
      this.panel.appendChild(ghost);
    }
    if (scroll) ghost.scrollIntoView({ behavior: scrollBehavior, block: "start" });
    return false;
  }

  /** Swap to a different scenario URL (used by dev-mode scenario toggle). */
  async loadUrl(url) {
    this.briefUrl = url;
    return this.load();
  }

  /** Extract & merge all <style> tags from the brief document. */
  _extractStyles(doc) {
    const styles = doc.querySelectorAll("style");
    let combined = "";
    styles.forEach((s) => {
      combined += s.textContent + "\n";
    });
    return combined;
  }

  /**
   * Extract <body> content as a string, stripping engine's refresh-bar and
   * refresh-fab (PWA owns refresh UX now) and the inline annotation script.
   */
  _extractBody(doc) {
    const body = doc.body;
    if (!body) return "";

    // Drop refresh chrome — PWA shell owns this
    body.querySelectorAll(".refresh-bar, .refresh-fab").forEach((el) => el.remove());

    // Drop scripts — they're scaffold-era refresh hooks and will be re-implemented
    // PWA-side via the TileManager/BriefLoader later. Don't execute someone else's
    // <script> inside our shell unintentionally.
    body.querySelectorAll("script").forEach((el) => el.remove());

    return body.innerHTML;
  }

  /** Pull engine metadata from the brief's <meta> tags. */
  _extractMeta(doc) {
    const get = (name) =>
      doc.querySelector(`meta[name="${name}"]`)?.getAttribute("content") || null;
    return {
      course: get("x-beacon-course"),
      date: get("x-beacon-date"),
      tee: get("x-beacon-tee"),
      specVersion: get("annotation-spec-version"),
    };
  }

  /**
   * Inject a single <style> element with the engine's CSS, scoped under
   * SCOPE_SELECTOR. Replaces the previous one if already injected.
   */
  _renderStyle(rawCss) {
    const scoped = scopeCss(rawCss, SCOPE_SELECTOR);

    if (!this._styleEl) {
      this._styleEl = document.createElement("style");
      this._styleEl.dataset.beaconBriefStyle = "true";
      document.head.appendChild(this._styleEl);
    }
    this._styleEl.textContent = scoped;
  }

  /**
   * BEACON-50 v0.7 (UX-F2): editorial error UX with named affordances.
   * Replaces raw fetch-error string ("HTTP 404", "TypeError: Failed to fetch")
   * with declarative copy + retry + switch-course buttons. The user always
   * has a path forward, never a dead-end with a stack trace.
   */
  _renderError(err) {
    const courseName = this.courseDisplayName || "this course";
    const isOffline = typeof navigator !== "undefined" && navigator.onLine === false;
    const headline = isOffline
      ? "You’re offline."
      : `Couldn’t load the brief for ${escapeHtml(courseName)}.`;
    const detail = isOffline
      ? "The brief will load once you reconnect."
      : "Try again, or switch to a different course.";
    this.panel.innerHTML = `
      <div class="brief-error" role="alert">
        <h3>${escapeHtml(headline)}</h3>
        <p>${escapeHtml(detail)}</p>
        <div class="brief-error-actions">
          <button type="button" class="is-primary" data-action="retry">Retry</button>
          <button type="button" data-action="switch-course">Switch course</button>
        </div>
      </div>
    `;
    const retryBtn = this.panel.querySelector('[data-action="retry"]');
    if (retryBtn) {
      retryBtn.addEventListener("click", () => {
        this.panel.innerHTML = `<p class="brief-placeholder">Loading…</p>`;
        this.panel.setAttribute("aria-busy", "true");
        this.load()
          .then(() => this.panel.setAttribute("aria-busy", "false"))
          .catch(() => this.panel.setAttribute("aria-busy", "false"));
      });
      retryBtn.focus();
    }
    const switchBtn = this.panel.querySelector('[data-action="switch-course"]');
    if (switchBtn) {
      switchBtn.addEventListener("click", () => {
        const sel = document.getElementById("course-select");
        if (sel) sel.focus();
      });
    }
    // Log technical detail to console only — not surfaced to users (BC V2).
    if (err) console.warn("[BriefLoader] load failed:", err);
  }
}

/**
 * Scope a CSS string under a parent selector. Handles:
 *   - Top-level rules (selector { ... }) → prefix selector with parent
 *   - body { ... } / body:foo / body[…] → replace with parent (engine uses body
 *     for the brief container)
 *   - @media / @supports / @keyframes wrappers — preserved intact, inner rules
 *     scoped recursively
 *   - @font-face, @import, @page — preserved unchanged (don't take selectors)
 *
 * This is not a full CSS parser — it's a pragmatic regex-based pass that
 * handles the engine's hand-written CSS. If we ever switch to a CSS-in-JS
 * pipeline, this whole function disappears.
 */
function scopeCss(css, parent) {
  // Strip CSS comments so our regex isn't fooled by braces inside them
  css = css.replace(/\/\*[\s\S]*?\*\//g, "");

  // Walk top-level: split by balanced {} blocks
  const out = [];
  let i = 0;
  const len = css.length;

  while (i < len) {
    // Skip whitespace
    while (i < len && /\s/.test(css[i])) i++;
    if (i >= len) break;

    // Handle at-rules
    if (css[i] === "@") {
      const atEnd = findAtRuleEnd(css, i);
      const atBlock = css.slice(i, atEnd);
      out.push(scopeAtRule(atBlock, parent));
      i = atEnd;
      continue;
    }

    // Regular rule: find selector then balanced block
    const braceStart = css.indexOf("{", i);
    if (braceStart === -1) break; // dangling content
    const selector = css.slice(i, braceStart).trim();
    const blockEnd = findMatchingBrace(css, braceStart);
    if (blockEnd === -1) break;
    let body = css.slice(braceStart + 1, blockEnd).trim();

    // If the selector was `body` / `body:foo` / `body[…]` / `html` (i.e. an
    // engine root rule that targets the document), strip layout-affecting
    // declarations that would fight the PWA shell's own #brief-panel sizing.
    // The engine's `body { max-width: 720px; margin: 1.5rem auto; padding: 0
    // 1rem }` was overflowing the mobile viewport because source order had
    // it winning the specificity tie against our app CSS.
    if (/^(?:body\b|html\b)/.test(selector.split(",")[0].trim())) {
      body = stripLayoutDecls(body);
    }

    out.push(`${prefixSelector(selector, parent)} { ${body} }`);
    i = blockEnd + 1;
  }

  return out.join("\n");
}

/**
 * Strip CSS declarations that would fight the PWA shell's layout when the
 * engine targeted document-level selectors (body / html). The PWA owns
 * sizing for `#brief-panel`; we only want the engine's typography, color,
 * and spacing-of-children rules — not its width/margin/padding box-model
 * decls that assume a centered standalone document.
 */
const _LAYOUT_DECL_NAMES = new Set([
  "max-width",
  "min-width",
  "width",
  "max-height",
  "min-height",
  "height",
  "margin",
  "margin-top",
  "margin-right",
  "margin-bottom",
  "margin-left",
  "margin-block",
  "margin-inline",
  "padding",
  "padding-top",
  "padding-right",
  "padding-bottom",
  "padding-left",
  "padding-block",
  "padding-inline",
  "position",
  "top",
  "right",
  "bottom",
  "left",
  "display",
  "box-sizing",
]);

function stripLayoutDecls(body) {
  return body
    .split(";")
    .map((decl) => decl.trim())
    .filter((decl) => {
      if (!decl) return false;
      const colonIdx = decl.indexOf(":");
      if (colonIdx === -1) return false;
      const name = decl.slice(0, colonIdx).trim().toLowerCase();
      return !_LAYOUT_DECL_NAMES.has(name);
    })
    .map((decl) => decl + ";")
    .join(" ");
}

/** Prefix a (possibly-comma-separated) selector list with parent scope. */
function prefixSelector(selector, parent) {
  return selector
    .split(",")
    .map((sel) => {
      sel = sel.trim();
      if (!sel) return sel;
      // body / body:foo / body[…] → just use parent
      if (/^body\b/.test(sel)) {
        return parent + sel.slice(4); // keeps :foo / [attr]
      }
      // html → drop entirely (PWA owns html)
      if (/^html\b/.test(sel)) {
        return parent;
      }
      // Already scoped → leave alone
      if (sel.startsWith(parent)) return sel;
      // :root → use parent (so CSS vars stay accessible inside scope)
      if (sel === ":root") return parent;
      // Default: nest under parent
      return `${parent} ${sel}`;
    })
    .join(", ");
}

/** Scope an @media / @supports / @keyframes block. */
function scopeAtRule(atBlock, parent) {
  const headerEnd = atBlock.indexOf("{");
  if (headerEnd === -1) {
    // Standalone at-rule (@import, @charset) — keep as-is
    return atBlock;
  }
  const header = atBlock.slice(0, headerEnd).trim();
  const closeIdx = atBlock.lastIndexOf("}");
  const inner = atBlock.slice(headerEnd + 1, closeIdx);

  // @keyframes / @font-face — inner shouldn't be selector-prefixed
  if (/^@(keyframes|-webkit-keyframes|-moz-keyframes|font-face|page|counter-style|property)\b/.test(header)) {
    return atBlock;
  }

  return `${header} {\n${scopeCss(inner, parent)}\n}`;
}

function findMatchingBrace(s, openIdx) {
  let depth = 0;
  for (let k = openIdx; k < s.length; k++) {
    if (s[k] === "{") depth++;
    else if (s[k] === "}") {
      depth--;
      if (depth === 0) return k;
    }
  }
  return -1;
}

/**
 * Find the end of an at-rule starting at idx. Handles both block
 * (@media{...}) and statement (@import "...";) forms.
 */
function findAtRuleEnd(css, idx) {
  // Look for first { or ; after the at-rule name
  let k = idx;
  while (k < css.length && css[k] !== "{" && css[k] !== ";") k++;
  if (k >= css.length) return css.length;
  if (css[k] === ";") return k + 1;
  // Block form — find matching }
  const closeIdx = findMatchingBrace(css, k);
  return closeIdx === -1 ? css.length : closeIdx + 1;
}

function escapeHtml(s) {
  return String(s).replace(/[&<>"']/g, (c) => ({
    "&": "&amp;",
    "<": "&lt;",
    ">": "&gt;",
    '"': "&quot;",
    "'": "&#39;",
  }[c]));
}
